import { ChangeEvent, FormEvent, useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';
import {
    Alert,
    backendServices,
    Button,
    Dialog,
    isBuyerPortalFeatureEnabled,
    PasswordField,
    TextField,
    useBuyerPortalSettings,
    useFetchSupplierSIMRequest,
} from 'common';
import { routes } from '../../../signup-portal.routes';
import { clearSignInState, signInWithPassword } from '../PostSignup/useSilentSignIn';
import { openSimRequestParams } from '../PostSignup/useSupplierPendingSimRequests';

const FORM_ID = 'finish-documents-form';

const SIGN_IN_FAILED_MESSAGE = 'Error logging in. Please contact Transcepta support.';
const LOAD_FAILED_MESSAGE = 'Error loading your account information. Please contact Transcepta support.';
const NO_VENDOR_MESSAGE = "Your account isn't set up with this customer yet. Please contact Transcepta support.";

// Only buyers with vendor approval create vendors for self-registered suppliers.
async function fetchVendorId(buyerCompanyId: number, supplierCompanyId: number): Promise<number | null> {
    try {
        const { data } = await new backendServices.Apis.CompanyApi().getVendors({
            companyId: buyerCompanyId,
            supplierCompanyId,
        });
        return data.Items[0]?.Id ?? null;
    } catch {
        return null;
    }
}

export function FinishDocumentsDialog({
    open,
    onClose,
    companyToken,
    buyerCompanyId,
    buyerName,
}: {
    open: boolean;
    onClose(): void;
    companyToken: string;
    buyerCompanyId: number;
    buyerName: string;
}) {
    const history = useHistory();
    const fetchSupplierSIMRequest = useFetchSupplierSIMRequest();
    const { data: buyerPortalSettings } = useBuyerPortalSettings({ companyId: buyerCompanyId });

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAllSubmitted, setIsAllSubmitted] = useState(false);

    // TIN and ACH validation are saved against the vendor.
    const requiresVendor =
        isBuyerPortalFeatureEnabled(buyerPortalSettings, 'EnablePostSignupTinValidation') ||
        isBuyerPortalFeatureEnabled(buyerPortalSettings, 'EnablePostSignupAchValidation');

    const handleClose = () => {
        onClose();
        setError(null);
        setIsAllSubmitted(false);
    };

    const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
        setIsAllSubmitted(false);
    };

    const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);
        setIsAllSubmitted(false);
    };

    const fail = (message: string) => {
        clearSignInState();
        setError(message);
        setIsSubmitting(false);
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        setError(null);
        setIsAllSubmitted(false);

        let companyId: number;
        try {
            const login = await signInWithPassword(email, password);
            companyId = parseInt(login.companyId, 10);
        } catch {
            fail(SIGN_IN_FAILED_MESSAGE);
            return;
        }

        let hasOpenRequests: boolean;
        try {
            const openRequests = await fetchSupplierSIMRequest(openSimRequestParams(companyId));
            hasOpenRequests = openRequests.some((request) => request.BuyerCompanyId === buyerCompanyId);
        } catch {
            fail(LOAD_FAILED_MESSAGE);
            return;
        }
        if (!hasOpenRequests) {
            clearSignInState();
            setIsAllSubmitted(true);
            setIsSubmitting(false);
            return;
        }

        let company: backendServices.ViewModels.CompanyViewModel | undefined;
        try {
            const { data } = await new backendServices.Apis.CompanyApi().getCompany(companyId);
            company = data[0];
        } catch {
            fail(LOAD_FAILED_MESSAGE);
            return;
        }
        if (company == null) {
            fail(LOAD_FAILED_MESSAGE);
            return;
        }

        const submissionMethodKey =
            backendServices.ViewModels.SubmissionMethod[company.DefaultInvoiceSubmissionMethod] ?? '';

        const vendorId = await fetchVendorId(buyerCompanyId, companyId);
        if (vendorId == null && requiresVendor) {
            fail(NO_VENDOR_MESSAGE);
            return;
        }

        history.push(routes.supplierPostSignup.path.replaceAll(':companyToken', companyToken), {
            alreadySignedIn: true,
            companyId,
            companyName: company.Name,
            vendorId,
            buyerCompanyId,
            submissionMethodKey,
        });
    };

    return (
        <Dialog
            open={open}
            title="Finish your documents"
            handleOnClose={handleClose}
            testId="finish-documents-dialog"
            content={
                <Stack component="form" id={FORM_ID} onSubmit={handleSubmit} spacing={2}>
                    <Typography>Enter the email and password you used to register with {buyerName}.</Typography>
                    <TextField
                        label="Login Email"
                        value={email}
                        onChange={handleEmailChange}
                        autoComplete="username"
                        required
                        testId="finish-documents-email"
                    />
                    <PasswordField
                        label="Password"
                        value={password}
                        onChange={handlePasswordChange}
                        autoComplete="current-password"
                        required
                        testId="finish-documents-password"
                    />
                    {error && (
                        <Alert severity="error" testId="finish-documents-error">
                            {error}
                        </Alert>
                    )}
                    {isAllSubmitted && (
                        <Alert severity="success" testId="finish-documents-all-submitted">
                            All requested documents have already been submitted.
                        </Alert>
                    )}
                </Stack>
            }
            actions={
                <Button
                    type="submit"
                    form={FORM_ID}
                    disabled={isSubmitting || !email || !password || isAllSubmitted}
                    startIcon={isSubmitting && <CircularProgress size="1rem" />}
                >
                    Continue
                </Button>
            }
        />
    );
}
